import React, { useState } from 'react';
import { Modal, Button, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faLock, faTrash } from '@fortawesome/free-solid-svg-icons';
import type { AccountDangerSectionProps } from '../types';
import {
  FormSection,
  DangerSection,
  DangerSectionTitle,
  DangerSectionDescription,
  DangerBox,
  DangerBoxIcon,
  DangerBoxContent,
  DangerBoxTitle,
  DangerBoxDescription,
  DangerButton,
  DeactivateButton,
  ConfirmationInput,
  WarningBox,
  WarningTextSmall,
  DangerActionsContainer,
  ModalTitleRow,
  ModalIcon,
  ModalDeleteList,
  ModalConfirmWrapper,
  ModalConfirmLabel,
} from '../Styles';

// ─── Sub-components ───────────────────────────────────────────────────────────

const DangerModalTitle: React.FC<{ icon: any; color: string; title: string }> = ({ icon, color, title }) => (
  <ModalTitleRow>
    <ModalIcon $color={color}>
      <FontAwesomeIcon icon={icon} />
    </ModalIcon>
    <span>{title}</span>
  </ModalTitleRow>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AccountDangerSection: React.FC<AccountDangerSectionProps> = ({
  loading = false,
  onDeactivate,
  onDelete,
}) => {
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deactivateInput, setDeactivateInput] = useState('');
  const [deleteInput, setDeleteInput] = useState('');

  const openDeactivate = () => { setDeactivateInput(''); setDeactivateModal(true); };
  const closeDeactivate = () => { setDeactivateModal(false); setDeactivateInput(''); };
  const openDelete = () => { setDeleteInput(''); setDeleteModal(true); };
  const closeDelete = () => { setDeleteModal(false); setDeleteInput(''); };

  const handleDeactivateConfirm = () => {
    if (deactivateInput.toLowerCase() === 'deactivate') {
      onDeactivate?.();
      closeDeactivate();
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteInput.toLowerCase() === 'delete') {
      onDelete?.();
      closeDelete();
    }
  };

  return (
    <FormSection>
      <DangerSection>
        <DangerSectionTitle>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          Danger Zone
        </DangerSectionTitle>
        <DangerSectionDescription>
          These actions cannot be undone. Please proceed with extreme caution.
        </DangerSectionDescription>

        <DangerActionsContainer>
          <DangerBox $isDanger={false}>
            <DangerBoxIcon>
              <FontAwesomeIcon icon={faLock} />
            </DangerBoxIcon>
            <DangerBoxContent>
              <DangerBoxTitle>Deactivate Account</DangerBoxTitle>
              <DangerBoxDescription>
                Temporarily disable your account. Your data will be preserved and you can reactivate anytime by logging in.
              </DangerBoxDescription>
            </DangerBoxContent>
            <DeactivateButton onClick={openDeactivate} loading={loading}>
              Deactivate
            </DeactivateButton>
          </DangerBox>

          <DangerBox $isDanger={true}>
            <DangerBoxIcon>
              <FontAwesomeIcon icon={faTrash} />
            </DangerBoxIcon>
            <DangerBoxContent>
              <DangerBoxTitle>Delete Account</DangerBoxTitle>
              <DangerBoxDescription>
                Permanently remove your account and all associated data. This action is irreversible.
              </DangerBoxDescription>
            </DangerBoxContent>
            <DangerButton onClick={openDelete} loading={loading}>
              Delete
            </DangerButton>
          </DangerBox>
        </DangerActionsContainer>
      </DangerSection>

      {/* Deactivate Modal */}
      <Modal
        title={<DangerModalTitle icon={faLock} color="#ff9800" title="Deactivate Your Account" />}
        open={deactivateModal}
        onCancel={closeDeactivate}
        width={500}
        footer={[
          <Button key="cancel" onClick={closeDeactivate}>Cancel</Button>,
          <DeactivateButton
            key="confirm"
            onClick={handleDeactivateConfirm}
            disabled={deactivateInput.toLowerCase() !== 'deactivate'}
            loading={loading}
          >
            Yes, Deactivate
          </DeactivateButton>,
        ]}
      >
        <DangerActionsContainer>
          <Typography.Text>
            Your account will be temporarily suspended. You can reactivate it anytime by logging back in with your credentials.
          </Typography.Text>
          <WarningBox $type="warning">
            <WarningTextSmall>
              <strong>What happens:</strong> Dashboard access will be blocked, but all your data will be preserved.
            </WarningTextSmall>
          </WarningBox>
          <ModalConfirmWrapper>
            <ModalConfirmLabel>
              To confirm, type <strong style={{ color: '#ff9800' }}>deactivate</strong>
            </ModalConfirmLabel>
            <ConfirmationInput
              placeholder='Type "deactivate" to confirm'
              value={deactivateInput}
              onChange={(e) => setDeactivateInput(e.target.value)}
              status={deactivateInput && deactivateInput.toLowerCase() !== 'deactivate' ? 'error' : undefined}
            />
          </ModalConfirmWrapper>
        </DangerActionsContainer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        title={<DangerModalTitle icon={faTrash} color="#f5222d" title="Delete Account Permanently" />}
        open={deleteModal}
        onCancel={closeDelete}
        width={500}
        footer={[
          <Button key="cancel" onClick={closeDelete}>Cancel</Button>,
          <DangerButton
            key="confirm"
            onClick={handleDeleteConfirm}
            disabled={deleteInput.toLowerCase() !== 'delete'}
            loading={loading}
          >
            Yes, Delete Everything
          </DangerButton>,
        ]}
      >
        <DangerActionsContainer>
          <Typography.Text strong>
            This will permanently delete your account and all data:
          </Typography.Text>
          <ModalDeleteList>
            <li>Profile information and settings</li>
            <li>All inventory, items, and customizations</li>
            <li>Transaction and order history</li>
            <li>Uploaded files and media</li>
            <li>Account preferences and API keys</li>
          </ModalDeleteList>
          <WarningBox $type="danger">
            <WarningTextSmall>
              <strong>⚠️ Warning:</strong> This action cannot be undone. All your data will be permanently deleted.
            </WarningTextSmall>
          </WarningBox>
          <ModalConfirmWrapper>
            <ModalConfirmLabel>
              To confirm, type <strong style={{ color: '#f5222d' }}>delete</strong>
            </ModalConfirmLabel>
            <ConfirmationInput
              placeholder='Type "delete" to confirm'
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              status={deleteInput && deleteInput.toLowerCase() !== 'delete' ? 'error' : undefined}
            />
          </ModalConfirmWrapper>
        </DangerActionsContainer>
      </Modal>
    </FormSection>
  );
};

export default AccountDangerSection;
